"use client";

import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";

import { layoutBoxSize } from "@/lib/layout-size";

export type TextMetricsItem = {
  id: string;
  text: string;
  /**
   * Typography for this item. The hook puts it on the measuring element, so the
   * caller spreads this same object into the element it really renders and the
   * measured and the painted text cannot drift apart.
   */
  style: CSSProperties;
  /**
   * Width the text may wrap in. Leave it out to measure the unwrapped,
   * intrinsic size instead.
   */
  wrapWidth?: number;
};

const MEASURE_LAYER_STYLE: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  overflow: "hidden",
  visibility: "hidden",
  pointerEvents: "none",
};

/**
 * Measures real text instead of estimating it from character counts, which is
 * wrong in both directions and clips labels. The returned layer must be
 * rendered inside the caller so the text inherits the surrounding font.
 *
 * Two properties this rests on:
 *
 * - The measuring elements are sized by text, font and an optional wrap width
 *   alone, never by the layout that consumes the result. That is what keeps
 *   this from turning into a measure-layout-measure feedback loop.
 * - The result commits in a layout effect, before paint, so the first painted
 *   frame after a change already uses it. useEffect would paint one frame of
 *   the stale layout first.
 *
 * `sizes` is always populated for every item: measured once the layer has been
 * read, the caller's `fallback` until then.
 */
export function useTextMetrics({
  axis,
  items,
  fallback,
}: {
  axis: "width" | "height";
  items: TextMetricsItem[];
  fallback: (item: TextMetricsItem) => number;
}): { layer: ReactElement; sizes: Record<string, number> } {
  const elements = useRef(new Map<string, HTMLDivElement>());
  const [measured, setMeasured] = useState<Record<string, number>>({});

  useLayoutEffect(() => {
    const read = () => {
      setMeasured((prev) => {
        const next: Record<string, number> = {};
        let changed = false;
        items.forEach((item) => {
          const el = elements.current.get(item.id);
          if (!el) return;
          const size = layoutBoxSize(el, axis);
          // A collapsed or display:none ancestor makes the element report 0.
          // Holding the last good value stops the layout snapping shut while
          // the text is temporarily unmeasurable.
          next[item.id] = size > 0 ? size : (prev[item.id] ?? 0);
          if (Math.abs((prev[item.id] ?? 0) - next[item.id]) > 0.5) {
            changed = true;
          }
        });
        if (Object.keys(prev).length !== Object.keys(next).length) {
          changed = true;
        }
        return changed ? next : prev;
      });
    };

    read();
    const ro = new ResizeObserver(read);
    items.forEach((item) => {
      const el = elements.current.get(item.id);
      if (el) ro.observe(el);
    });
    return () => ro.disconnect();
  }, [axis, items]);

  const sizes = useMemo(() => {
    const out: Record<string, number> = {};
    items.forEach((item) => {
      out[item.id] = measured[item.id] ?? fallback(item);
    });
    return out;
  }, [items, measured, fallback]);

  const layer = (
    <div aria-hidden style={MEASURE_LAYER_STYLE}>
      {items.map((item) => (
        <div
          key={item.id}
          ref={(el) => {
            if (el) elements.current.set(item.id, el);
            else elements.current.delete(item.id);
          }}
          style={{
            ...item.style,
            ...(item.wrapWidth === undefined
              ? { whiteSpace: "nowrap", width: "max-content" }
              : { width: `${item.wrapWidth}px` }),
          }}
        >
          {item.text}
        </div>
      ))}
    </div>
  );

  return { layer, sizes };
}
